/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback } from 'react';
import { Linking, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { showSuccess } from '../../../shared/ui/appToast';

export type MerchantPayoutInfo = {
  amount: number;
  alias: string | null;
  cbu: string | null;
  init_point: string | null;
  rider_name: string | null;
  message?: string;
};

type AssignmentLite = {
  payout_status: string;
  rider_earning: number;
  rider_name?: string | null;
};

type Props = {
  assignment: AssignmentLite;
  tipAmount: number;
  payoutInfo: MerchantPayoutInfo | null;
  payoutLoading: boolean;
  onLoadPayout: () => void;
  onMarkPaid: () => void;
  /** Estilos CTA: desk usa primaryCtaDesk, mobile primaryCtaMobile. */
  variant: 'desk' | 'mobile';
};

function hasTransferTarget(info: MerchantPayoutInfo): boolean {
  return !!(info.alias?.trim() || info.cbu?.trim() || info.init_point?.trim());
}

/** Box de pago al rider con estados due / pending_confirm / disputed / sin datos. */
export function MerchantRiderPayoutBox({
  assignment,
  tipAmount,
  payoutInfo,
  payoutLoading,
  onLoadPayout,
  onMarkPaid,
  variant,
}: Props) {
  const status = assignment.payout_status;
  const overdue = status === 'overdue';
  const pendingConfirm = status === 'pending_confirm';
  const disputed = status === 'disputed';
  const canMarkPaid = status === 'due' || status === 'overdue' || status === 'disputed';

  const copy = useCallback(async (label: string, value: string) => {
    await Clipboard.setStringAsync(value);
    showSuccess('Copiado', `${label} copiado`);
  }, []);

  const titleSuffix = overdue ? ' · vencido' : disputed ? ' · en disputa' : '';

  return (
    <View style={styles.payoutBox}>
      <Text style={styles.payoutTitle}>
        Pagar repartidor · ${assignment.rider_earning}
        {titleSuffix}
      </Text>
      {(assignment.rider_name || payoutInfo?.rider_name) ? (
        <Text style={styles.payoutLine}>
          Repartidor: {assignment.rider_name ?? payoutInfo?.rider_name}
        </Text>
      ) : null}
      <Text style={styles.payoutLine}>
        Transferile al repartidor. Es aparte del total del pedido.
      </Text>
      {tipAmount > 0 ? (
        <Text style={styles.payoutLine}>Incluye propina ${tipAmount}</Text>
      ) : null}

      {pendingConfirm ? (
        <Text style={styles.payoutStatusOk}>
          Esperando al repartidor. Ya marcaste la transferencia.
        </Text>
      ) : null}
      {disputed ? (
        <Text style={styles.payoutStatusWarn}>
          El repartidor marcó que no le llegó. Revisá los datos y transferí de nuevo si hace falta.
        </Text>
      ) : null}

      {payoutInfo ? (
        hasTransferTarget(payoutInfo) ? (
          <>
            {payoutInfo.alias ? (
              <TouchableOpacity
                onPress={() => void copy('Alias', payoutInfo.alias!)}
                accessibilityRole="button"
              >
                <Text style={styles.payoutLine}>Alias: {payoutInfo.alias} · Copiar</Text>
              </TouchableOpacity>
            ) : null}
            {payoutInfo.cbu ? (
              <TouchableOpacity
                onPress={() => void copy('CBU', payoutInfo.cbu!)}
                accessibilityRole="button"
              >
                <Text style={styles.payoutLine}>CBU: {payoutInfo.cbu} · Copiar</Text>
              </TouchableOpacity>
            ) : null}
            {payoutInfo.init_point ? (
              <TouchableOpacity onPress={() => void Linking.openURL(payoutInfo.init_point!)}>
                <Text style={styles.payoutLink}>Abrir Mercado Pago</Text>
              </TouchableOpacity>
            ) : null}
            {canMarkPaid ? (
              <TouchableOpacity
                style={variant === 'desk' ? styles.primaryCtaDesk : styles.primaryCtaMobile}
                onPress={onMarkPaid}
                disabled={payoutLoading}
              >
                <Text
                  style={variant === 'desk' ? styles.primaryCtaText : styles.primaryCtaMobileText}
                >
                  Ya transferí
                </Text>
              </TouchableOpacity>
            ) : null}
          </>
        ) : (
          <Text style={styles.payoutStatusWarn}>
            El repartidor no cargó alias ni CBU. No hay a quién transferir.
          </Text>
        )
      ) : (
        <TouchableOpacity style={styles.rejectCta} onPress={onLoadPayout} disabled={payoutLoading}>
          <Text style={styles.rejectCtaText}>
            {payoutLoading ? 'Cargando…' : 'Ver datos de pago'}
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  payoutBox: {
    borderWidth: 1,
    borderColor: ticketColors.gold,
    borderRadius: 10,
    padding: 14,
    gap: 8,
    backgroundColor: ticketColors.paper,
  },
  payoutTitle: {
    color: ticketColors.ink,
    fontSize: 15,
    fontWeight: '800',
    fontFamily: ticketFonts.sans,
  },
  payoutLine: {
    color: ticketColors.ink2,
    fontSize: 13,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  payoutLink: {
    color: ticketColors.gold,
    fontSize: 14,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  payoutStatusOk: {
    color: ticketColors.ok,
    fontSize: 13,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  payoutStatusWarn: {
    color: ticketColors.bad,
    fontSize: 13,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  rejectCta: {
    borderWidth: 1,
    borderColor: ticketColors.bad,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: 'center',
  },
  rejectCtaText: { color: ticketColors.bad, fontSize: 15, fontWeight: '700' },
  primaryCtaDesk: {
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center',
  },
  primaryCtaText: {
    color: ticketColors.goldInk,
    fontSize: 16,
    fontWeight: '900',
  },
  primaryCtaMobile: {
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    height: 56,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryCtaMobileText: {
    color: ticketColors.goldInk,
    fontSize: 16,
    fontWeight: '900',
    letterSpacing: 0.8,
  },
});
